import { getCities, getCityAreas, getStoreInfo, getStoreFileList, downloadStoreFile, getStoreServicesList } from "@/services/api.service";
import { markNoticeAsShown } from '@/utils/storeNotice.utils'
import { StoresFilesRes } from '@/models/storeImage.model'

export default {
    namespaced: true,
    state: {
        errorLogin: {
            state: false,
            message: "",
        },
        currentStore: null,
        invoiceCarrierState: false,
        npoBanState: false,
        selectedAddressData: null,
        cities: [],
        cityAreas: [],
        storeInfo: {},
        // 店家圖片狀態
        storeImages: {
            storeInfoImage: null,    // 店家資訊圖片 Base64 字串（可持久化）
            loading: false,          // 載入狀態
            error: null,            // 錯誤訊息
            lastFetchTime: null     // 最後載入時間（用於快取）
        },
        // 店家服務清單
        storeServicesList: [],
    },
    getters: {
        errorLoginState: (state) => state.errorLogin.state,
        errorLoginMessage: (state) => state.errorLogin.message,
        // 取得商店名稱(可能與 code 不同，會有中文名稱)
        storeName: (state) => {
            try {
                return state.storeInfo.name || state.currentStore || '';
            } catch (error) {
                return '';
            }
        },
        googleSheetId: (state) => {
            const sheetId = state.storeInfo.sheet_url_scan
            if (!sheetId) return ''
            // 使用 regexp 取得 sheet id
            // sheet_url_online 會以 https://docs.google.com/spreadsheets/d/ 開頭
            // 並結束在下一個出現的 / 字元
            const sheetIdRegexp = /https:\/\/docs.google.com\/spreadsheets\/d\/([^/]+)/;
            const match = sheetId.match(sheetIdRegexp) || [];
            return match[1] || '';
        },
        // 取得門市菜單的 Google Sheet ID（使用 sheet_url）
        storeMenuSheetId: (state) => {
            const sheetUrl = state.storeInfo.sheet_url;
            if (!sheetUrl) return '';

            // 使用 regexp 取得 sheet id
            const sheetIdRegexp = /https:\/\/docs\.google\.com\/spreadsheets\/d\/([^/]+)/;
            const match = sheetUrl.match(sheetIdRegexp) || [];
            return match[1] || '';
        },
        serviceFeeMode: (state) => {
            return state.storeInfo.service_fee_mode || 0;
        },
        serviceFee: (state) => {
            return state.storeInfo.service_fee || 0;
        },
        serviceFeeRate: (state, getters) => {
            return getters.serviceFee / 100;
        },
        // 運費
        shippingFee: (state) => {
            return state.storeInfo.shipping_fee || 0;
        },
        // 滿額免運門檻
        freeShipping: (state) => {
            return state.storeInfo.free_shipping || 0;
        },
        storeBusinessHours: (state, getters) => {
            if (!state.storeInfo.business_hours) return [];
            const businessHours = JSON.parse(state.storeInfo.business_hours)
            return getters.businessHoursHandler(businessHours.arrangements || [])
        },
        businessHoursHandler: () => (businessHours) => {
            if (!businessHours) return [];
            const newBusinessHours = []
            // 如果資料中 startTime 大於 endTime，則需創造一筆新的資料 (跨日)
            // 比較 startTime 與 endTime 時需注意兩者都是字串 "HH:mm"
            // 並將這筆資料的 endTime 設為 24:00，新的資料的 startTime 設為 00:00 和 endTime 設為原資料的 endTime
            // 並將新資料的 weekDays 每個都 +1，需注意如果原資料的 weekDays 為 7，則需設為 1
            businessHours.forEach(item => {
                const startTime = item.startTime.split(':');
                const endTime = item.endTime.split(':');
                if (startTime[0] > endTime[0] || (startTime[0] === endTime[0] && startTime[1] >= endTime[1])) {
                    newBusinessHours.push({
                        ...item,
                        startTime: '00:00',
                        weekDays: item.weekDays.map(i => i === 7 ? 1 : i + 1)
                    });
                    item.endTime = '24:00';
                }
            });
            // 合併原本的 businessHours 與新的 newBusinessHours，並依照 startTime 排序
            return [...businessHours, ...newBusinessHours].sort((a, b) => {
                const aStartTime = a.startTime.split(':');
                const bStartTime = b.startTime.split(':');
                return aStartTime[0] - bStartTime[0] || aStartTime[1] - bStartTime[1];
            });
        },
        mondayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(1) ? item : false).filter(i => i !== false) || [])
        },
        mondayStartTime: (state, getters) => {
            return getters.mondayBusinessHours.map(item => item.startTime) || [];
        },
        mondayEndTime: (state, getters) => {
            return getters.mondayBusinessHours.map(item => item.endTime) || [];
        },
        tuesdayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(2) ? item : false).filter(i => i !== false) || [])
        },
        tuesdayStartTime: (state, getters) => {
            return getters.tuesdayBusinessHours.map(item => item.startTime) || [];
        },
        tuesdayEndTime: (state, getters) => {
            return getters.tuesdayBusinessHours.map(item => item.endTime) || [];
        },
        wednesdayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(3) ? item : false).filter(i => i !== false) || [])
        },
        wednesdayStartTime: (state, getters) => {
            return getters.wednesdayBusinessHours.map(item => item.startTime) || [];
        },
        wednesdayEndTime: (state, getters) => {
            return getters.wednesdayBusinessHours.map(item => item.endTime) || [];
        },
        thursdayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(4) ? item : false).filter(i => i !== false) || [])
        },
        thursdayStartTime: (state, getters) => {
            return getters.thursdayBusinessHours.map(item => item.startTime) || [];
        },
        thursdayEndTime: (state, getters) => {
            return getters.thursdayBusinessHours.map(item => item.endTime) || [];
        },
        fridayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(5) ? item : false).filter(i => i !== false) || [])
        },
        fridayStartTime: (state, getters) => {
            return getters.fridayBusinessHours.map(item => item.startTime) || [];
        },
        fridayEndTime: (state, getters) => {
            return getters.fridayBusinessHours.map(item => item.endTime) || [];
        },
        saturdayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(6) ? item : false).filter(i => i !== false) || [])
        },
        saturdayStartTime: (state, getters) => {
            return getters.saturdayBusinessHours.map(item => item.startTime) || [];
        },
        saturdayEndTime: (state, getters) => {
            return getters.saturdayBusinessHours.map(item => item.endTime) || [];
        },
        sundayBusinessHours: (state, getters) => {
            return getters.businessHoursMergeHandler(getters.storeBusinessHours.map(item => item.weekDays.includes(7) ? item : false).filter(i => i !== false) || [])
        },
        sundayStartTime: (state, getters) => {
            return getters.sundayBusinessHours.map(item => item.startTime) || [];
        },
        sundayEndTime: (state, getters) => {
            return getters.sundayBusinessHours.map(item => item.endTime) || [];
        },
        businessHoursMergeHandler: () => (businessHours) => {
            // 處理當天的營業時間
            // 如果一筆資料的 endTime 等於下一筆 startTime，則合併兩筆資料
            // 並將合併後的 endTime 設為下一筆資料的 endTime
            const item = JSON.parse(JSON.stringify(businessHours));
            for (let i = 0; i < item.length - 1; i++) {
                if (item[i].endTime === item[i + 1].startTime) {
                    item[i].endTime = item[i + 1].endTime;
                    item.splice(i + 1, 1);
                    i--;
                }
            }
            return item;
        },
        todayBusinessHours: (state, getters) => {
            const today = new Date().getDay();
            if (today === 0) {
                return getters.sundayBusinessHours
            } else if (today === 1) {
                return getters.mondayBusinessHours
            } else if (today === 2) {
                return getters.tuesdayBusinessHours
            } else if (today === 3) {
                return getters.wednesdayBusinessHours
            } else if (today === 4) {
                return getters.thursdayBusinessHours
            } else if (today === 5) {
                return getters.fridayBusinessHours
            } else if (today === 6) {
                return getters.saturdayBusinessHours
            }
            return [];
        },
        todayStoreClose: (state, getters) => {
            if (getters.todayBusinessHours.every(i => i === false)) {
                return true;
            }
            return false;
        },
        tomorrowBusinessHours: (state, getters) => {
            const today = new Date().getDay();
            if (today === 6) {
                return getters.sundayBusinessHours
            } else if (today === 0) {
                return getters.mondayBusinessHours
            } else if (today === 1) {
                return getters.tuesdayBusinessHours
            } else if (today === 2) {
                return getters.wednesdayBusinessHours
            } else if (today === 3) {
                return getters.thursdayBusinessHours
            } else if (today === 4) {
                return getters.fridayBusinessHours
            } else if (today === 5) {
                return getters.saturdayBusinessHours
            }
            return [];
        },
        tomorrowStoreClose: (state, getters) => {
            if (getters.tomorrowBusinessHours.every(i => i === false)) {
                return true;
            }
            return false;
        },
        appEnableFunction: (state) => {
            return state.storeInfo.app_enabled_function || 0
        },
        isFunctionEnabled: () => (decimal, bitPosition) => {
            // 使用 bit 位數判斷十進位值在二進位值的 value
            if (decimal === undefined) return false
            return (decimal & (1 << bitPosition)) !== 0;
        },
        enableDineIn: (state, getters) => {
            return getters.isFunctionEnabled(getters.appEnableFunction, 0);
        },
        enableToGo: (state, getters) => {
            return getters.isFunctionEnabled(getters.appEnableFunction, 1);
        },
        enablePickUp: (state, getters) => {
            return getters.isFunctionEnabled(getters.appEnableFunction, 2);
        },
        enableDelivery: (state, getters) => {
            return getters.isFunctionEnabled(getters.appEnableFunction, 3);
        },
        enableRetailCheckout: (state, getters) => {
            return getters.isFunctionEnabled(getters.appEnableFunction, 4);
        },
        enableRetailDelivery: (state, getters) => {
            return getters.isFunctionEnabled(getters.appEnableFunction, 5);
        },
        activeOnlineOrder: (state, getters) => {
            return getters.isFunctionEnabled(state.storeInfo.actived_config, 0)
        },
        // 2026/04/07 調整為 isServiceEnabled 判斷，此 getter 已不再使用
        // activePointShop: (state, getters) => {
        //     return getters.isFunctionEnabled(state.storeInfo.actived_config, 1)
        // },
        // 2026/04/07 調整為 isServiceEnabled 判斷，此 getter 已不再使用
        // enableOnlineOrder: (state) => {
        //     return state.storeInfo.enabled_function > 1
        // },
        onlineOrderConfig: (state) => {
            return JSON.parse(state.storeInfo.online_order_config) || {}
        },
        pickUpDelayTime: (state, getters) => {
            return getters.onlineOrderConfig['2'] || 0
        },
        pickUpDelayMinute: (state, getters) => {
            if (getters.pickUpDelayTime < 60) {
                return getters.pickUpDelayTime;
            }
            return getters.pickUpDelayTime % 60;
        },
        pickUpDelayHour: (state, getters) => {
            if (getters.pickUpDelayTime < 60) {
                return 0;
            }
            return Math.floor(getters.pickUpDelayTime / 60);
        },
        toGoDelayTime: (state, getters) => {
            return getters.onlineOrderConfig['1'] || 0
        },
        toGoDelayMinute: (state, getters) => {
            if (getters.toGoDelayTime < 60) {
                return getters.toGoDelayTime;
            }
            return getters.toGoDelayTime % 60;
        },
        toGoDelayHour: (state, getters) => {
            if (getters.toGoDelayTime < 60) {
                return 0;
            }
            return Math.floor(getters.toGoDelayTime / 60);
        },
        // 外送延遲時間（分鐘）
        deliveryDelayTime: (state, getters) => {
            return getters.onlineOrderConfig['3'] || 0
        },
        // 外送延遲分鐘數
        deliveryDelayMinute: (state, getters) => {
            if (getters.deliveryDelayTime < 60) {
                return getters.deliveryDelayTime;
            }
            return getters.deliveryDelayTime % 60;
        },
        // 外送延遲小時數
        deliveryDelayHour: (state, getters) => {
            if (getters.deliveryDelayTime < 60) {
                return 0;
            }
            return Math.floor(getters.deliveryDelayTime / 60);
        },
        // 新增：取得公告內容
        noticeContent: (state) => {
            return state.storeInfo.notice || ''
        },
        // 新增：檢查公告是否啟用
        noticeEnabled: (state) => {
            return state.storeInfo.notice_enabled || false
        },
        // 新增：判斷是否啟用發票選項
        enableInvoiceSelector: (state) => {
            return state.storeInfo?.additional_features_switch === 1
        },
        // 社群媒體連結 getters
        lineOfficialAddress: (state) => state.storeInfo.line_official_address || '',
        storeFacebook: (state) => state.storeInfo.store_facebook || '',
        storeInstagram: (state) => state.storeInfo.store_ig || '',
        storeOfficialWeb: (state) => state.storeInfo.store_official_web || '',
        storeYoutube: (state) => state.storeInfo.store_youtube || '',
        // 店家簡介
        storeDescription: (state) => state.storeInfo.store_desc || '',
        // 店家圖片 getters
        storeInfoImageUrl: (state) => state.storeImages.storeInfoImage,
        storeImagesLoading: (state) => state.storeImages.loading,
        storeImagesError: (state) => state.storeImages.error,
        hasStoreInfoImage: (state) => !!state.storeImages.storeInfoImage,
        // 依 service_type 查詢是否啟用（is_enabled 為 true 且 expire_at 未過期）
        isServiceEnabled: (state) => (serviceType) => {
            const service = state.storeServicesList.find(s => s.service_type === serviceType);
            if (!service) return false;
            if (!service.is_enabled) return false;
            // expire_at 為 null 時視為永不過期
            if (service.expire_at === null) return true;
            return new Date(service.expire_at) > new Date();
        },
    },
    mutations: {
        setErrorLogin(state, errorLogin) {
            state.errorLogin = errorLogin;
        },
        setInvoiceCarrierState(state, invoiceCarrierState) {
            state.invoiceCarrierState = invoiceCarrierState;
        },
        setNpoBanState(state, npoBanState) {
            state.npoBanState = npoBanState;
        },
        setSelectedAddressData(state, selectedAddressData) {
            state.selectedAddressData = selectedAddressData;
        },
        setCurrentStore(state, currentStore) {
            state.currentStore = currentStore;
        },
        setCities(state, cities) {
            state.cities = cities;
        },
        setCityAreas(state, cityAreas) {
            state.cityAreas = cityAreas;
        },
        setStoreInfo(state, storeInfo) {
            state.storeInfo = storeInfo;
        },
        // 店家圖片 mutations
        setStoreInfoImage(state, imageData) {
            state.storeImages.storeInfoImage = imageData?.base64 || null;
            state.storeImages.lastFetchTime = new Date().toISOString();
        },
        setStoreImagesLoading(state, loading) {
            state.storeImages.loading = loading;
        },
        setStoreImagesError(state, error) {
            state.storeImages.error = error;
        },
        clearStoreInfoImage(state) {
            // 清理店家資訊圖片資料
            state.storeImages.storeInfoImage = null;
            state.storeImages.error = null;
            state.storeImages.lastFetchTime = null;
        },
        setStoreServicesList(state, list) {
            state.storeServicesList = list;
        },
    },
    actions: {
        setErrorLogin({ commit }, errorLogin) {
            commit("setErrorLogin", errorLogin);
        },
        async fetchCities({ commit }) {
            const res = await getCities();
            if (res?.data?.msg === 'Failed') {
                throw res?.data?.err_msg;
            }
            commit('setCities', res?.data?.data?.cities);
        },
        async fetchCityAreas({ commit }) {
            const res = await getCityAreas();
            if (res?.data?.msg === 'Failed') {
                throw res?.data?.err_msg;
            }
            commit('setCityAreas', res?.data?.data?.cityareas);
        },
        async fetchStoreInfo({ commit }) {
            const res = await getStoreInfo();
            if (res?.data?.msg === 'Failed') {
                throw res?.data?.err_msg;
            }
            commit('setStoreInfo', res?.data?.data?.store_info);
        },
        // 新增：標記公告已顯示
        markNoticeAsShown({ rootGetters }) {
            const userIdentifier = rootGetters['userData/storeMemberUuid']
            const storeCode = rootGetters['userData/storeCode']

            if (userIdentifier && storeCode) {
                markNoticeAsShown(userIdentifier, storeCode)
            }
        },
        // 店家圖片 actions
        async fetchStoreImages({ commit, rootGetters }) {
            // 檢查是否登入（只有登入時才載入新圖片）
            const isLogin = rootGetters['userData/isLogin']
            if (!isLogin) {
                console.log('未登入，使用快取的店家圖片（如果有）')
                return
            }

            // 移除快取時間檢查，每次都重新載入（確保資料即時更新）
            console.log('重新載入店家圖片以獲取最新資訊');

            commit('setStoreImagesLoading', true);
            commit('setStoreImagesError', null);

            try {
                // 1. 取得檔案列表
                const response = await getStoreFileList();

                // 檢查回應狀態
                if (response?.data?.data?.msg === 'Failed') {
                    // 店家沒有設定照片，清除快取
                    console.log('店家沒有設定照片，清除圖片快取');
                    commit('clearStoreInfoImage');
                    return; // 不拋出錯誤，正常結束
                }

                const filesRes = new StoresFilesRes(response.data);
                const files = filesRes?.data?.files || [];

                // 2. 尋找固定的店家資訊圖片檔案
                const targetFileName = 'StoreInfoImage.jpg';
                const imageFiles = files.filter(file => file.fileName === targetFileName);

                if (imageFiles.length === 0) {
                    console.log('未找到 StoreInfoImage.jpg 檔案');
                    commit('clearStoreInfoImage');
                    return;
                }

                // 3. 下載店家資訊圖片並轉換為 Base64
                const targetFile = imageFiles[0]; // 只處理第一個找到的檔案

                try {
                    // 檢查檔案大小（5MB = 5 * 1024 * 1024 bytes）
                    const maxSize = 5 * 1024 * 1024;
                    if (targetFile.fileSize > maxSize) {
                        const errorMsg = `圖片檔案過大: ${targetFile.fileName} (${(targetFile.fileSize / 1024 / 1024).toFixed(2)}MB)`;
                        console.warn(errorMsg);
                        commit('setStoreImagesError', errorMsg);
                        return;
                    }

                    const response = await downloadStoreFile(targetFile.fileName);

                    // 檢查回應是否為圖片
                    if (response.data && response.data.type.startsWith('image/')) {
                        // 將 Blob 轉換為 Base64
                        const base64 = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(response.data);
                        });

                        const imageData = {
                            fileName: targetFile.fileName,
                            fileSize: targetFile.fileSize,
                            base64: base64, // Base64 字串（包含 data:image/xxx;base64, 前綴）
                            downloadTime: new Date().toISOString()
                        };

                        commit('setStoreInfoImage', imageData);
                        console.log(`成功載入店家資訊圖片: ${targetFile.fileName}（Base64 格式）`);
                    } else {
                        console.warn('下載的檔案不是圖片格式:', targetFile.fileName);
                        commit('setStoreImagesError', '檔案格式不正確');
                    }
                } catch (error) {
                    console.error('下載或轉換檔案失敗:', targetFile.fileName, error);
                    commit('setStoreImagesError', '下載圖片失敗');
                }
            } catch (error) {
                const errorMessage = error?.message || '載入店家圖片失敗';
                console.error('載入店家圖片失敗:', errorMessage);
                commit('setStoreImagesError', errorMessage);
            } finally {
                commit('setStoreImagesLoading', false);
            }
        },
        // 清理店家圖片
        clearStoreImages({ commit }) {
            commit('clearStoreInfoImage');
        },
        // 取得店家服務清單
        async fetchStoreServicesList({ commit }) {
            try {
                const res = await getStoreServicesList();
                commit('setStoreServicesList', res.data?.data?.store_services_list ?? []);
            } catch (e) {
                console.error('fetchStoreServicesList failed:', e);
            }
        },
    },
};